/**
 * VIB-2545: roving tabindex i matriser (RBAC-kortene). Ren funksjon som gir
 * neste celle for en piltast/Home/End, og hopper over celler som ikke finnes
 * eller er disabled. `null` = tasten er ikke en gridtast, eller ingen
 * gyldig celle i den retningen (fokus blir stående).
 */
export interface GridCell {
  row: number;
  col: number;
}

export type GridNavKey = "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight" | "Home" | "End";

export function isGridNavKey(key: string): key is GridNavKey {
  return (
    key === "ArrowUp" ||
    key === "ArrowDown" ||
    key === "ArrowLeft" ||
    key === "ArrowRight" ||
    key === "Home" ||
    key === "End"
  );
}

export type CellEnabled = (row: number, col: number) => boolean;

export function nextGridCell(
  from: GridCell,
  key: GridNavKey,
  rows: number,
  cols: number,
  isEnabled: CellEnabled = () => true,
): GridCell | null {
  const ok = (r: number, c: number) => r >= 0 && r < rows && c >= 0 && c < cols && isEnabled(r, c);
  const scan = (dr: number, dc: number): GridCell | null => {
    let r = from.row + dr;
    let c = from.col + dc;
    while (r >= 0 && r < rows && c >= 0 && c < cols) {
      if (ok(r, c)) return { row: r, col: c };
      r += dr;
      c += dc;
    }
    return null;
  };
  switch (key) {
    case "ArrowUp":
      return scan(-1, 0);
    case "ArrowDown":
      return scan(1, 0);
    case "ArrowLeft":
      return scan(0, -1);
    case "ArrowRight":
      return scan(0, 1);
    case "Home":
      for (let c = 0; c < cols; c++) if (ok(from.row, c)) return c === from.col ? null : { row: from.row, col: c };
      return null;
    case "End":
      for (let c = cols - 1; c >= 0; c--) if (ok(from.row, c)) return c === from.col ? null : { row: from.row, col: c };
      return null;
  }
}

/** Første aktive celle i leserekkefølge — der Tab lander inn i matrisen. */
export function firstEnabledCell(rows: number, cols: number, isEnabled: CellEnabled = () => true): GridCell | null {
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) if (isEnabled(r, c)) return { row: r, col: c };
  return null;
}
